import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import { colors, customShadows, borderRadius, borderRadiusPx } from "../../../../theme";

/**
 * PositionSummaryCard - Displays itemized financial, tax, or staking positions with highlights.
 */
const PositionSummaryCard = ({
  title = "",
  items = [],
  currencyPrefix = "",
  currencySuffix = "",
  sx = {},
}) => {
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: borderRadiusPx.card,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardSubtle,
        backgroundColor: colors.dashboard.cardBg,
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
        {title && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              color: colors.dashboard.title,
              mb: 2,
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>
        )}

        {items && items.length > 0 && (
          <Stack spacing={1.25} sx={{ flex: 1 }}>
            {items.map((item, index) => {
              const isRed = item.highlight === "error" || item.isOutstanding;
              const isBold = item.isBold || isRed;
              const valColor = isRed
                ? colors.error.main
                : item.highlightColor || (isBold ? colors.slate[900] : colors.slate[700]);

              return (
                <React.Fragment key={index}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 0.25 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: isRed ? colors.error.main : colors.slate[500],
                        fontWeight: isBold ? 600 : 400,
                        fontSize: "0.82rem",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: valColor,
                        fontWeight: isBold ? 700 : 500,
                        fontSize: "0.82rem",
                      }}
                    >
                      {item.rawPrefix !== undefined ? item.rawPrefix : currencyPrefix}
                      {item.value}
                      {item.rawSuffix !== undefined ? item.rawSuffix : currencySuffix}
                    </Typography>
                  </Stack>
                  {item.hasDivider && <Divider sx={{ my: 0.5, borderColor: colors.dashboard.divider }} />}
                </React.Fragment>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

PositionSummaryCard.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      highlight: PropTypes.string,
      highlightColor: PropTypes.string,
      isOutstanding: PropTypes.bool,
      isBold: PropTypes.bool,
      hasDivider: PropTypes.bool,
      rawPrefix: PropTypes.string,
      rawSuffix: PropTypes.string,
    })
  ),
  currencyPrefix: PropTypes.string,
  currencySuffix: PropTypes.string,
  sx: PropTypes.object,
};

export default PositionSummaryCard;
